import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getData, putData } from '../Services/api';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useProfile } from '../Context/ProfileContext';
import { GEORGIA_BOLD_BASE64 } from './georgiaFont';

const formatTimeAMPM = (timeStr) => {
    if (!timeStr) return '';
    const str = String(timeStr).trim();
    if (/am|pm/i.test(str)) return str;
    const parts = str.split(':');
    if (parts.length >= 2) {
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1].substring(0, 2).padStart(2, '0');
        if (!isNaN(hours)) {
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12 || 12;
            const hoursStr = String(hours).padStart(2, '0');
            return `${hoursStr}:${minutes} ${ampm}`;
        }
    }
    return str;
};

const ViewPO = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { profile } = useProfile();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const handleGenerateInvoice = async () => {
        Swal.fire({
            title: "Generate Invoice",
            text: order?.status === 'cancelled'
                ? "Are you sure you want to generate the invoice PDF copy for this CANCELLED Proforma Order? This will assign a sequence number."
                : "Are you sure you want to generate the invoice for this Proforma Order?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, generate!",
            cancelButtonText: "Cancel",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setSubmitting(true);
                try {
                    const res = await putData(`/proforma-orders/generate-invoice/${id}`, {});
                    if (res.status) {
                        toast.success(res.message);
                        navigate('/accounts-dashboard/view-edit');
                    } else {
                        toast.error(res.message || "Failed to generate invoice");
                    }
                } catch (error) {
                    console.error("Error generating invoice:", error);
                    toast.error(error.response?.data?.message || "Failed to generate invoice");
                } finally {
                    setSubmitting(false);
                }
            }
        });
    };

    const generateInvoicePDF = (order) => {
        try {
            // Orientation: landscape, A4 dimensions: 297mm x 210mm
            const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            
            try {
                if (GEORGIA_BOLD_BASE64) {
                    doc.addFileToVFS('Georgia-Bold.ttf', GEORGIA_BOLD_BASE64);
                    doc.addFont('Georgia-Bold.ttf', 'Georgia', 'bold');
                }
            } catch (fontErr) {
                console.warn("Could not register Georgia font:", fontErr);
            }
            
            const width = 133;
            // Swapped Code and Product Name, minimized Code to 12 and gave the rest to Product Name (51)
            const colWidths = [10, 12, 51, 12, 12, 16, 20];
            const colAlignments = ["center", "center", "left", "center", "center", "right", "right"];

            let products = [];
            try {
                products = typeof order.products === 'string' ? JSON.parse(order.products) : (order.products || []);
            } catch (e) {
                console.error("Failed to parse products", e);
            }

            const drawRow = (startX, y, rowData, bgFillColor) => {
                if (bgFillColor) {
                    doc.setFillColor(bgFillColor[0], bgFillColor[1], bgFillColor[2]);
                    doc.rect(startX, y, width, 8, 'F');
                }
                doc.rect(startX, y, width, 8); // outer border
                
                let currentX = startX;
                for (let i = 0; i < colWidths.length; i++) {
                    const w = colWidths[i];
                    const text = String(rowData[i] !== undefined && rowData[i] !== null ? rowData[i] : '');
                    const align = colAlignments[i];
                    
                    if (i > 0) {
                        doc.line(currentX, y, currentX, y + 8);
                    }
                    
                    let textX = currentX + 2;
                    if (align === "center") {
                        textX = currentX + w / 2;
                    } else if (align === "right") {
                        textX = currentX + w - 2;
                    }
                    
                    doc.setFontSize(10); // Increase Font Size of Product List to 10
                    
                    if (i === 2 && text !== "Product Name") { // Product Name column text wrapping
                        const lines = doc.splitTextToSize(text, w - 3);
                        if (lines.length > 1) {
                            doc.text(lines, textX, y + 3.5, { align: align });
                        } else {
                            doc.text(text, textX, y + 5.5, { align: align });
                        }
                    } else {
                        doc.text(text, textX, y + 5.5, { align: align });
                    }
                    currentX += w;
                }
            };

            const drawTotalRow = (startX, y, totalAmt) => {
                doc.rect(startX, y, width, 8);
                doc.line(startX + 85, y, startX + 85, y + 8);
                doc.line(startX + 113, y, startX + 113, y + 8);
                
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.text("Total", startX + 85 + 14, y + 5.5, { align: "center" });
                
                doc.setTextColor(255, 0, 0);
                doc.text(Number(totalAmt).toLocaleString(), startX + 113 + 18, y + 5.5, { align: "right" });
            };

            const companyName = order.resolved_company_name || order.createdByUser?.company_name || profile?.company_name || 'Company Name';

            const drawInvoiceSection = (startX, isDuplicate) => {
                // 1. Company Name Row (y = 10 to 18)
                doc.setDrawColor(0, 0, 0);
                doc.setLineWidth(0.2);
                doc.rect(startX, 10, width, 8);
                doc.setFont("helvetica", "bold");
                doc.setFontSize(14);
                doc.setTextColor(255, 0, 0);
                doc.text(companyName, startX + width / 2, 16, { align: "center" });

                // 2. Type Row (y = 18 to 26) with different color coding for each PO type
                doc.rect(startX, 18, 10, 8);
                doc.setFontSize(9);
                doc.setTextColor(0, 0, 0);
                doc.setFont("helvetica", "bold");
                doc.text("Type", startX + 5, 23.5, { align: "center" });

                let typeBgColor = [255, 255, 0]; // Default Yellow
                let typeTextColor = [255, 0, 0]; // Default Red
                if (order.po_type_color && order.po_type_color.toLowerCase() !== '#ffffff') {
                    const hexToRgb = (hex) => {
                        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
                        return result ? [
                            parseInt(result[1], 16),
                            parseInt(result[2], 16),
                            parseInt(result[3], 16)
                        ] : [255, 255, 255];
                    };
                    const getContrastColor = (rgb) => {
                        const yiq = ((rgb[0] * 299) + (rgb[1] * 587) + (rgb[2] * 114)) / 1000;
                        return (yiq >= 128) ? [0, 0, 0] : [255, 255, 255];
                    };
                    typeBgColor = hexToRgb(order.po_type_color);
                    typeTextColor = getContrastColor(typeBgColor);
                } else if (order.po_type) {
                    const t = String(order.po_type).toUpperCase();
                    if (t.includes("DOMESTIC")) {
                        typeBgColor = [220, 245, 220]; // Light Green
                        typeTextColor = [0, 128, 0];
                    } else if (t.includes("EXPORT")) {
                        typeBgColor = [220, 235, 255]; // Light Blue
                        typeTextColor = [0, 0, 180];
                    } else if (t.includes("LOCAL")) {
                        typeBgColor = [255, 235, 205]; // Light Orange
                        typeTextColor = [160, 82, 45];
                    } else if (t.includes("SPECIAL")) {
                        typeBgColor = [240, 220, 255]; // Light Violet
                        typeTextColor = [128, 0, 128];
                    }
                }

                doc.setFillColor(typeBgColor[0], typeBgColor[1], typeBgColor[2]);
                doc.rect(startX + 10, 18, 123, 8, 'FD');
                doc.setFontSize(10);
                doc.setTextColor(typeTextColor[0], typeTextColor[1], typeTextColor[2]);
                doc.text(order.po_type || '-', startX + 10 + 123 / 2, 23.5, { align: "center" });

                // 3. Delivery Note Row (y = 26 to 34)
                doc.rect(startX, 26, width, 8);
                doc.setFontSize(11);
                doc.setTextColor(0, 0, 0);
                const titleText = isDuplicate ? "Delivery Note - Duplicate" : "Delivery Note";
                doc.text(titleText, startX + width / 2, 31.5, { align: "center" });

                // Compute invoice creation date & time (never PO date/time)
                let invoiceDateStr = '-';
                let datePart = '';
                let timePart = '';
                if (order.invoice_date) {
                    const s = String(order.invoice_date).trim();
                    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
                        const [y, m, d] = s.split('T')[0].split(' ')[0].split('-');
                        datePart = `${d}/${m}/${y}`;
                        if (!order.invoice_time && s.includes(' ')) {
                            timePart = formatTimeAMPM(s.split(' ')[1]);
                        }
                    } else if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
                        datePart = s;
                    } else {
                        const dObj = new Date(s);
                        if (!isNaN(dObj.getTime())) {
                            const y = dObj.getFullYear();
                            const m = String(dObj.getMonth() + 1).padStart(2, '0');
                            const d = String(dObj.getDate()).padStart(2, '0');
                            datePart = `${d}/${m}/${y}`;
                        } else {
                            datePart = s;
                        }
                    }
                }
                if (order.invoice_time) {
                    timePart = formatTimeAMPM(order.invoice_time);
                }
                if (!datePart && (order.invoice_number || order.status === 'invoiced') && order.updatedAt) {
                    const uDate = new Date(order.updatedAt);
                    if (!isNaN(uDate.getTime())) {
                        const y = uDate.getFullYear();
                        const mm = String(uDate.getMonth() + 1).padStart(2, '0');
                        const dd = String(uDate.getDate()).padStart(2, '0');
                        datePart = `${dd}/${mm}/${y}`;
                        if (!timePart) {
                            let hours = uDate.getHours();
                            const minutes = String(uDate.getMinutes()).padStart(2, '0');
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            hours = hours % 12 || 12;
                            timePart = `${hours}:${minutes} ${ampm}`;
                        }
                    }
                }
                if (datePart && timePart) {
                    invoiceDateStr = `${datePart}  ${timePart}`; // TWO spaces
                } else {
                    invoiceDateStr = datePart || timePart || '-';
                }

                // 4. Invoice Number & Date & Time Row (y = 34 to 42)
                doc.rect(startX, 34, width, 8);
                doc.line(startX + 28, 34, startX + 28, 42);
                doc.line(startX + 65, 34, startX + 65, 42);
                doc.line(startX + 88, 34, startX + 88, 42);

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(255, 0, 0);
                doc.text("Invoice Number", startX + 2, 39.5);
                doc.setTextColor(0, 0, 0);
                doc.text(order.invoice_number || 'PENDING', startX + 30, 39.5);

                doc.setTextColor(255, 0, 0);
                doc.text("Date & Time", startX + 67, 39.5);
                doc.setTextColor(0, 0, 0);
                doc.text(invoiceDateStr, startX + 90, 39.5);

                // 5. PO Number Row (y = 42 to 50)
                doc.rect(startX, 42, width, 8);
                doc.line(startX + 28, 42, startX + 28, 50);

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(255, 0, 0);
                doc.text("PO Number", startX + 2, 47.5);
                doc.setTextColor(0, 0, 0);
                doc.text(order.po_number || '-', startX + 30, 47.5);

                // 6. Customer Name Row (y = 50 to 58)
                doc.rect(startX, 50, width, 8);
                doc.line(startX + 30, 50, startX + 30, 58);

                doc.setFont("helvetica", "bold");
                doc.setFontSize(9);
                doc.setTextColor(255, 0, 0);
                doc.text("Customer Name", startX + 2, 55.5);
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(12); // Increase the customer’s name font size upto 12
                doc.text(order.customer_name || '', startX + 32, 55.5);

                // 7. Header Row (y = 58 to 66) - Code is now before Product Name
                const headerData = ["Sr No", "Code", "Product Name", "Qty", "Units", "Rate", "Amount"];
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                drawRow(startX, 58, headerData, [189, 215, 238]);

                // 8. Products Table (y = 66 onwards)
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                const totalRows = Math.max(products.length, 10);
                let currentY = 66;
                for (let i = 0; i < totalRows; i++) {
                    const prod = products[i] || {};
                    const srNo = i + 1;
                    const name = prod.product || prod.name || '';
                    const code = prod.code || '';
                    const qty = prod.qty || prod.quantity || '';
                    const unit = prod.unit || '';
                    const rate = prod.rate || prod.price || '';
                    const amt = prod.amount || (prod.qty * prod.rate) || '';

                    // Order: Sr No, Code, Name, Qty, Unit, Rate, Amount
                    const rowData = [srNo, code, name, qty, unit, rate, amt ? Number(amt).toLocaleString() : ''];
                    drawRow(startX, currentY, rowData, null);
                    currentY += 8;
                }

                // 9. Total Row
                drawTotalRow(startX, currentY, order.total_amount);

                // 10. Comments Box at bottom if present
                if (order.comments) {
                    const commentY = currentY + 12;
                    doc.setFont("helvetica", "normal");
                    doc.setFontSize(9);
                    doc.setTextColor(0, 0, 0);
                    doc.text("Comment: " + order.comments, startX, commentY, { maxWidth: width });
                }

                // 11. Mention "DUPLICATE" at bottom of duplicate invoice copy (Left side only)
                if (isDuplicate) {
                    try {
                        doc.setFont("Georgia", "bold");
                    } catch (e) {
                        doc.setFont("times", "bold");
                    }
                    doc.setFontSize(50);
                    doc.setTextColor(255, 0, 0);
                    let duplicateY = 198;
                    if (order.comments) {
                        const splitComments = doc.splitTextToSize("Comment: " + order.comments, width);
                        const commentsEndY = currentY + 12 + (splitComments.length * 4);
                        duplicateY = Math.max(198, commentsEndY + 12);
                    }
                    doc.text("DUPLICATE", startX + 2, duplicateY);
                }

                // Add CANCELLED Watermark if order is cancelled
                if (order.status === 'cancelled') {
                    doc.saveGraphicsState();
                    let hasOpacity = false;
                    try {
                        if (doc.GState) {
                            doc.setGState(new doc.GState({ opacity: 0.38 }));
                            hasOpacity = true;
                        }
                    } catch (e) {
                        console.warn("jsPDF GState error:", e);
                    }
                    if (hasOpacity) {
                        doc.setTextColor(215, 40, 55);
                    } else {
                        doc.setTextColor(220, 100, 110);
                    }
                    doc.setFont("helvetica", "bold");
                    doc.setFontSize(45);
                    doc.text("CANCELLED", startX + width / 2, 98, { align: "center", angle: 30 });
                    doc.restoreGraphicsState();
                }
            };

            // First mention "Delivery Note - Duplicate" copy page and then 2nd page "Delivery Note"
            // Left page is duplicate
            drawInvoiceSection(10, true);

            // Draw dashed vertical separator line in the middle
            doc.setDrawColor(150, 150, 150);
            doc.setLineDashPattern([2, 2], 0);
            doc.line(148.5, 10, 148.5, 200);
            doc.setLineDashPattern([], 0);

            // Right page is original
            drawInvoiceSection(154, false);

            // Save the PDF
            doc.save(`Invoice_${order.invoice_number || order.po_number}.pdf`);
            toast.success("PDF Downloaded successfully!");
        } catch (err) {
            console.error("PDF Gen Error:", err);
            toast.error("Failed to generate PDF. Check console for details.");
        }
    };

    const fetchOrderDetails = async () => {
        try {
            const res = await getData(`/proforma-orders/details/${id}`);
            if (res.status) {
                setOrder(res.data);
            }
        } catch (error) {
            console.error("Error fetching PO details:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrderDetails();
    }, [id]);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'invoiced': return 'bg-success';
            case 'ready_for_delivery': return 'bg-primary';
            case 'cancelled': return 'bg-danger';
            case 'checking': return 'bg-warning';
            default: return 'bg-secondary';
        }
    };

    if (loading) {
        return (
            <div className="pc-container">
                <div className="pc-content text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-3 text-muted">Loading Proforma Order details...</div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="pc-container">
                <div className="pc-content">
                    <div className="alert alert-danger">Proforma Order not found or access denied.</div>
                    <Link to="/accounts-dashboard/view-edit" className="btn btn-outline-secondary">Go Back</Link>
                </div>
            </div>
        );
    }

    // Parse products if string
    let products = [];
    try {
        products = typeof order.products === 'string' ? JSON.parse(order.products) : (order.products || []);
    } catch (e) {
        console.error("Failed to parse products", e);
    }

    return (
        <div className="pc-container">
            <div className="pc-content">
                {/* Breadcrumbs */}
                <div className="page-header">
                    <div className="page-block">
                        <div className="row align-items-center">
                            <div className="col-md-12">
                                <ul className="breadcrumb">
                                    <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                                    <li className="breadcrumb-item"><Link to="/accounts-dashboard/view-edit">Accounts</Link></li>
                                    <li className="breadcrumb-item" aria-current="page">PO Details</li>
                                </ul>
                            </div>
                            <div className="col-md-12">
                                <div className="page-header-title">
                                    <h2 className="mb-0">Proforma Order Details</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div className="row">
                    <div className="col-lg-4">
                        <div className="card">
                            <div className="card-header">
                                <h5>PO Info</h5>
                            </div>
                            <div className="card-body">
                                <ul className="list-group list-group-flush">
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">PO Number:</span>
                                        <span className="fw-bold">{order.po_number}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">PO Type:</span>
                                        <span className="fw-bold">{order.po_type || '-'}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Customer Name:</span>
                                        <span className="fw-bold">{order.customer_name}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Date & Time:</span>
                                        <span>{order.date} {formatTimeAMPM(order.time)}</span>
                                    </li>
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Status:</span>
                                        <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                                            {order.status.replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                    </li>
                                    {order.invoice_number && (
                                        <>
                                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                <span className="text-muted">Invoice Number:</span>
                                                <span className="fw-bold text-success">{order.invoice_number}</span>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                                <span className="text-muted">Invoice Date & Time:</span>
                                                <span className="fw-bold text-success">
                                                    {order.invoice_date || (order.updatedAt ? new Date(order.updatedAt).toISOString().split('T')[0] : '-')} {order.invoice_time || (order.updatedAt ? formatTimeAMPM(new Date(order.updatedAt).toLocaleTimeString()) : '')}
                                                </span>
                                            </li>
                                        </>
                                    )}
                                    <li className="list-group-item d-flex justify-content-between align-items-center px-0">
                                        <span className="text-muted">Total Amount:</span>
                                        <span className="fw-bold text-primary fs-5">Tzs {Number(order.total_amount).toLocaleString('en-US')}</span>
                                    </li>
                                </ul>

                                <div className="d-grid gap-2 mt-4">
                                    {(order.status === 'invoiced' || (order.status === 'cancelled' && order.invoice_number)) && (
                                        <button
                                            type="button"
                                            className="btn btn-danger text-white"
                                            onClick={() => generateInvoicePDF(order)}
                                        >
                                            <i className="fas fa-download me-2" /> Download PDF
                                        </button>
                                    )}
                                    {order.status !== 'invoiced' && order.status !== 'cancelled' && (
                                        <Link to={`/accounts-dashboard/edit-po/${order.id}`} className="btn btn-success">
                                            <i className="fas fa-edit me-2" /> Edit PO
                                        </Link>
                                    )}
                                    <Link to="/accounts-dashboard/view-edit" className="btn btn-outline-secondary">
                                        <i className="fas fa-arrow-left me-2" /> Back to List
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-lg-8">
                        <div className="card">
                            <div className="card-header">
                                <h5>Itemized Products</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-hover mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Code</th>
                                                <th>Product</th>
                                                <th className="text-center">Qty</th>
                                                <th>Unit</th>
                                                <th className="text-end">Rate</th>
                                                <th className="text-end">Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {products.length > 0 ? (
                                                products.map((prod, idx) => (
                                                    <tr key={idx}>
                                                        <td>{idx + 1}</td>
                                                        <td>{prod.code || '-'}</td>
                                                        <td className="fw-semibold">{prod.product || prod.name || '-'}</td>
                                                        <td className="text-center">{prod.qty || prod.quantity || 0}</td>
                                                        <td>{prod.unit || '-'}</td>
                                                        <td className="text-end">Tzs {Number(prod.rate || prod.price || 0).toLocaleString('en-US')}</td>
                                                        <td className="text-end fw-bold">Tzs {Number(prod.amount || ((prod.qty || prod.quantity || 0) * (prod.rate || prod.price || 0))).toLocaleString('en-US')}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4 text-muted">No products found in this Proforma Order.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                        {products.length > 0 && (
                                            <tfoot>
                                                <tr className="table-light">
                                                    <td colSpan="6" className="text-end fw-bold">Grand Total:</td>
                                                    <td className="text-end fw-bold text-primary fs-6">Tzs {Number(order.total_amount).toLocaleString('en-US')}</td>
                                                </tr>
                                            </tfoot>
                                        )}
                                    </table>
                                </div>
                            </div>
                        </div>

                        {(order.status !== 'invoiced' && (order.status !== 'cancelled' || !order.invoice_number)) && (
                            <div className="card mt-4">
                                <div className="card-header">
                                    <h5>Generate Invoice</h5>
                                </div>
                                <div className="card-body">
                                    {order.comments && (
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold">Salesperson Comment:</label>
                                            <div className="p-3 bg-light border rounded text-muted">
                                                {order.comments}
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-primary px-4"
                                            disabled={submitting}
                                            onClick={handleGenerateInvoice}
                                        >
                                            {submitting ? 'Generating...' : 'Save & Submit'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewPO;
